import { FC, useState, useRef } from 'react'
import { Button, AptiveIcon, Modal, Radio, Toast } from '@aptive-env/storybook'
import { format } from 'date-fns'

import { fetchUserAvatar, deleteCalendarEvent, overrideCalendarEvent } from 'services/calendar'
import { ICalendarEvent, ICalendarEventParticipant } from 'interface/calendar'
import AsyncRenderOnScroll from 'components/shared/AsyncRenderOnScroll'
import { EventUpdateModal } from 'components/EventUpdateModal'
import { eventConstants } from 'constants/values'
import { convert24HourTo12Hour } from 'utils/convertHour'

const { EVENT_EDIT_TYPE, EVENT_OVERRIDE_TYPE } = eventConstants

const LoadingComponent = () => {
  return <div className="w-[32px] h-[32px] rounded-full bg-ui-gray-200 flex items-center justify-center">
    <img
      src="/assets/image/avatar/loading.gif"
      alt="Loading ..."
      className="w-[10px]"
    />
  </div>
}

interface EventDetailModalProps {
  open: boolean
  event: ICalendarEvent
  eventDate: Date
  participants: ICalendarEventParticipant[]
  loading: boolean
  onDelete: () => void
  onClose: () => void
  onSuccess: (success: string) => void
  onHideShow: (eventId: number, eventDate: Date, isAll: boolean, isHidden: boolean) => void
}

const EventDetailModal: FC<EventDetailModalProps> = ({
  open,
  event,
  eventDate,
  participants,
  loading,
  onClose,
  onSuccess,
  onDelete,
  onHideShow
}) => {
  const [isDelete, setIsDelete] = useState(false)
  const [delEvent, setDelEvent] = useState(false)

  const [isEventUpdateModalOpen, setIsEventUpdateModalOpen] =
    useState<boolean>(false)
  const [eventUpdateType, setEventUpdateType] = useState<string>('')

  const [showDeleteToast, setShowDeleteToast] = useState(false)
  const [isDeleteCancelled, setIsDeleteCancelled] = useState(false)
  const [showErrorToast, setShowErrorToast] = useState(false)
  const isDeleteCancelledRef = useRef(isDeleteCancelled)
  isDeleteCancelledRef.current = isDeleteCancelled

  const handleClose = () => {
    onClose()
  }

  const handleDelClose = () => {
    setIsDelete(false)
  }

  const handleDelete = async () => {
    setShowDeleteToast(true)
    onHideShow(event.id, eventDate, delEvent, true)
    
    setTimeout(() => {
      setShowDeleteToast(false)
      if(isDeleteCancelledRef.current) {
        setIsDeleteCancelled(false)
      } else {
        if(delEvent) 
          deleteCalendarEvent(event.id)
            .then((response) => {
              onDelete()
              onSuccess(response.result.message)
              setIsDeleteCancelled(false)
            })
            .catch((error) => {
              console.log(error)
              setShowErrorToast(true)
            })
        else
          overrideCalendarEvent({
            eventId: event.id,
            date: format(eventDate, 'yyyy-MM-dd'), // example: 2024-01-01
            start_time: event.start_at, // example: 08:00:00
            end_time: event.end_at, // example: 08:30:00
            title: event.title,
            description: event.description,
            is_canceled: true,
            location_lat: event.location?.lat ? event?.location?.lat : 0,
            location_lng: event.location?.lng ? event?.location?.lng : 0,
            meeting_link: '',
            address: event.address?.address,
            city: event.address?.city,
            state: event.address?.state,
            zip: event.address?.zip
          })
            .then((response) => {
              onDelete()
              onSuccess(response.result.message)
              setIsDelete(!response._metadata.success)
              setIsDeleteCancelled(false)
            })
            .catch((error) => {
              console.log(error)
              setShowErrorToast(true)
            })

      }
    }, 5000)

    setIsDelete(false)
    onClose()
  }

  const handleUndoDelete = () => {
    setIsDeleteCancelled(true)
    setShowDeleteToast(false)
    onHideShow(event.id, eventDate, delEvent, false)
  }

  const handleEdit = () => {
    setEventUpdateType(EVENT_EDIT_TYPE)
    setIsEventUpdateModalOpen(true)
    onClose()
  }

  const handleOverride = () => {
    setEventUpdateType(EVENT_OVERRIDE_TYPE)
    setIsEventUpdateModalOpen(true)
    onClose()
  }

  const renderParticipants = (participant: ICalendarEventParticipant) => {
    if (participant?.external_id) {
      return (
        <AsyncRenderOnScroll
          loadingComponent={
            <div className="w-[32px] h-[32px] rounded-full bg-ui-gray-200 flex items-center justify-center">
              <img
                src="/assets/image/avatar/loading.gif"
                alt={participant.name[0]}
                className="w-[10px]"
              />
            </div>
          }
          fetchData={async () => {
            const response = await fetchUserAvatar(
              participant.external_id,
              true
            )
            return response.result.avatarBase64
              ? `data:image/png;base64,${response.result.avatarBase64}`
              : null
          }}
          render={(data) => {
            return data ? (
              <img
                className="w-[32px] h-[32px] rounded-full"
                src={data}
                alt=""
              />
            ) : (
              <div className="w-[32px] h-[32px] rounded-full bg-ui-gray-200 flex items-center justify-center">
                {participant.name[0]}
              </div>
            )
          }}
        />
      )
    }
    return (
      <div className="w-[32px] h-[32px] rounded-full bg-ui-gray-200 flex items-center justify-center">
        {participant.name[0]}
      </div>
    )
  }

  const handleSelectDeleteOption = (delEvent: boolean) => {
    setDelEvent(delEvent)
  }

  return (
    <>
      {
        open && 
          <Modal
            open={open}
            className="w-[576px] min-h-[372px] flex flex-col rounded-lg"
            onClickOutside={handleClose}
          >
            <div className={'p-5 flex flex-col grow text-gray-900'}>
              
              <div className="mb-4 flex justify-between">
                <div className="text-bodyBold">{event.title} | {event.event_type.charAt(0).toUpperCase() + event.event_type.slice(1)}</div>
                <AptiveIcon
                  icon="x"
                  className="w-6 h-6 stroke-[#3D3D3D] fill-none cursor-pointer"
                  onClick={handleClose}
                />
              </div>
              <div className='w-full h-[1px] bg-[#E5E7EB]'></div>
              
              <div className='mt-5 flex justify-between gap-6'>
                <div className='w-[50%] h-[216px] p-4 border border-[#E5E7EB] rounded-lg bg-[#F9FAFB]'>
                  <div className='flex items-center'>
                    <AptiveIcon
                      icon="calendar"
                      className="w-6 h-6 stroke-[#9CA3AF] fill-none cursor-pointer"
                    />
                    <p className='ml-2'>{format(eventDate, 'EEEE, MMMM dd')}</p>
                  </div>
                  <div className='mt-3 flex items-center'>
                    <AptiveIcon
                      icon="locationMarker"
                      className="w-6 h-6 stroke-[#9CA3AF] fill-none cursor-pointer"
                    />
                    <p className='ml-2'>{event?.address?.city}{event?.address?.city && event?.address ? ', ': ''} {event?.address?.state}</p>
                  </div>
                  <div className='mt-3'>
                    <p className='text-[#A9A9A9]'>Participants</p>
                    {
                      loading ? (<LoadingComponent />) : (<div className="flex mt-1 ml-[10px]">
                        {participants.slice(0, 5).map((participant) => (
                          <div className="h-[32px] w-[32px] flex-none rounded-full overflow-hidden bg-ui-gray-200 flex items-center justify-center ml-[-10px] border border-white" key={participant.id}>
                            {renderParticipants(participant)}
                          </div>
                        ))}
                        {participants.length > 5 && (
                          <div className="h-[32px] w-[32px] flex-none rounded-full overflow-hidden bg-ui-gray-500 flex items-center justify-center ml-[-10px] border border-white">
                            <div className="flex text-[13px] text-[#F9FAFB]">
                              <span>+</span>
                              <span>{participants.length - 5}</span>
                            </div>
                          </div>
                        )}
                      </div>)
                    }
                  </div>
                  <div className="mt-2">
                    <p className="text-[#A9A9A9]">Duration</p>
                    <p className="">
                      {convert24HourTo12Hour(event.start_at)} ~ {convert24HourTo12Hour(event.end_at)}
                    </p>
                  </div>
                </div>

                <div className="w-[50%]">
                  <div className="text-bodyBold">Details</div>
                  <p className="mt-4">
                    {event.description}
                  </p>
                </div>
              </div>

              <div className='mt-5 flex items-center justify-between'>
                <div>
                  <Button
                    variant="neutral"
                    onClick={handleEdit}
                    className="px-3 h-9 text-[#4B5563]"
                    position="left"
                    icon='pencilAlt'
                  >
                    Edit
                  </Button>
                  <Button
                    variant="neutral"
                    onClick={handleOverride}
                    className="px-3 h-9 text-[#4B5563] ml-3"
                    position="left"
                    icon='pencilAlt'
                  >
                    Override
                  </Button>
                </div>
                <Button
                  variant="danger"
                  onClick={() =>setIsDelete(true)}
                  className="px-3 h-9 text-[#4B5563]"
                  position="left"
                  icon='trash'
                >
                  Delete
                </Button>
              </div>
            </div>
          </Modal>
      }
      {isDelete && (
        <Modal
          open={isDelete}
          className="w-[512px] min-h-[172px] flex flex-col rounded-lg"
          onClickOutside={handleDelClose}
        >
          <div className="p-6 flex flex-col grow text-gray-900">
            <div className="text-bodyBold">Delete calendar event?</div>
            <p className="font-inter text-sm font-normal leading-5 text-dark-gray-100 mt-4 h-[40px]">
              Are you sure you want to delete this item? This action cannot be <br />undone.
            </p>
            <div className="flex flex-col gap-2 mt-5">
              <div>
                <Radio
                  checked={delEvent === false}
                  onChange={() => handleSelectDeleteOption(false)}
                >
                  This event
                </Radio>
              </div>
              <div>
                <Radio
                  checked={delEvent === true}
                  onChange={() => handleSelectDeleteOption(true)}
                >
                  All events
                </Radio>
              </div>
            </div>
            <div className="mt-5 flex items-center justify-end">
              <div>
                <Button
                  variant="neutral"
                  onClick={handleDelClose}
                  className="px-3 h-9 w-[88px] text-[#4B5563]"
                >
                  Cancel
                </Button>
              </div>
              <Button
                variant="danger"
                onClick={handleDelete}
                className="px-3 h-9 w-[88px] text-[#4B5563] ml-3"
              >
                Delete
              </Button>
            </div>
          </div>
        </Modal>
      )}
      {isEventUpdateModalOpen && (
        <EventUpdateModal
          isOpen={isEventUpdateModalOpen}
          setIsOpen={setIsEventUpdateModalOpen}
          updateType={eventUpdateType}
          event={event}
          onUpdate={onDelete}
          eventParticipants={participants}
          eventDate={eventDate}
          onSuccess={onSuccess}
        />
      )}
      <div className="fixed top-3 right-3 z-[100]">
        {
          showDeleteToast && (
            <Toast
              dismissible
              isIcon
              header="Success"
              isActionButton
              actionButtonTitle="Undo"
              onClickActionButton={handleUndoDelete}
              onDismiss={() => setShowDeleteToast(false)}
              status="success"
            >
              Well done, you successfully deleted a calendar event.
            </Toast>
          )          
        }
        {
          showErrorToast && (
            <Toast
              dismissible
              isIcon
              onDismiss={() => setShowErrorToast(false)}
              status="danger"
            >
              Something went wrong. Please try again later.
            </Toast>
          )          
        }
      </div>
    </>
  )
}

export default EventDetailModal
